import { useState } from 'react';
import { Badge, Button, Switch } from '@dreamtree-org/twreact-ui';
import { SectionCard, KeyValue, StatusBadge, StatCard } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { formatINRCompact } from '../../lib/format';
import SellPicker from '../../components/SellPicker';
import useSellCatalog from '../../components/useSellCatalog';

/**
 * Approved industries (MERCH-89): the store may turn on product types only within them. Unticking one never hides
 * what the store already sells; it only stops new types of that industry being turned on. Audited on save.
 */
function IndustriesCard({ merchant: m, act, busy, canManage }) {
  const sellCatalog = useSellCatalog();
  const saved = (m.industries || []).join();
  // Keyed on the saved list by its parent, so a save starts it again from the stored approval.
  const [chosen, setChosen] = useState(m.industries || []);
  // In registry order, as the server stores it.
  const dirty = sellCatalog.map((i) => i.key).filter((k) => chosen.includes(k)).join() !== saved;
  return (
    <SectionCard
      title="Approved industries"
      actions={canManage ? <Button size="sm" disabled={!dirty || Boolean(busy)} loading={busy === 'industries'} onClick={() => act('industries', 'platformMerchantIndustries', { industries: chosen })}>Save</Button> : null}
    >
      <p className="mb-2 text-xs opacity-60">The store can turn on product types only in these industries. Removing one keeps what it already sells.</p>
      {!saved ? <p className="mb-2 text-sm text-warning-600">None approved: the store cannot turn on any product type yet.</p> : null}
      <SellPicker catalog={sellCatalog} value={{ industries: chosen, types: [] }} onChange={(v) => setChosen(v.industries)} disabled={!canManage || Boolean(busy)} />
    </SectionCard>
  );
}

/** Identity, plan and a quick health summary; store counts are read from the store's own database. */
export default function OverviewTab({ merchant: m, act, busy, canManage }) {
  const catalog = useQuery('platformCapabilities', {}, { select: (r) => r.data });
  const caps = m.capabilities || {};
  const metrics = useQuery('platformMerchantMetrics', { id: m.id }, { select: (r) => r.data });
  const store = metrics.data && metrics.data.store;
  const verified = (m.domains || []).filter((d) => d.status === 'verified').length;
  const job = m.provisioning;
  const counts = (v) => (metrics.loading ? '…' : store ? v(store) : '—');
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Domains" value={`${verified} / ${(m.domains || []).length}`} hint="verified" />
        <StatCard label="Database" value={m.connection ? m.connection.status : '—'} hint={m.connection ? `schema ${m.connection.schema_version}` : 'no connection'} />
        <StatCard label="Units in stock" value={counts((s) => s.units)} hint={store ? `${store.listings} listings live` : 'store database'} />
        <StatCard label="GMV (30 days)" value={counts((s) => formatINRCompact(s.gmv_30d_paise))} hint={store ? `${store.orders_30d} paid orders` : 'store database'} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Business">
          <KeyValue items={[
            { label: 'Brand', value: m.display_name },
            { label: 'Legal name', value: m.legal_name },
            { label: 'GSTIN', value: m.gstin },
            { label: 'City', value: m.city },
            { label: 'Registered', value: formatDate(m.created_at) },
            { label: 'Company id', value: <code className="text-xs break-all">{m.id}</code> },
          ]} />
        </SectionCard>
        <SectionCard title="Contact & plan">
          <KeyValue items={[
            { label: 'Admin contact', value: m.contact_name },
            { label: 'Email', value: m.contact_email },
            { label: 'Phone', value: m.contact_phone },
            { label: 'Plan', value: <Badge variant="primary">{m.plan_key || '—'}</Badge> },
            { label: 'Status', value: <StatusBadge status={m.status} /> },
            { label: 'Provisioning', value: job ? <StatusBadge status={job.status} /> : '—' },
          ]} />
        </SectionCard>
        <SectionCard title="Seller capabilities">
          <div className="space-y-3">
            {((catalog.data && catalog.data.capabilities) || []).map((c) => (
              <div key={c.key}>
                <Switch
                  checked={Boolean(caps[c.key])}
                  disabled={!canManage || Boolean(busy)}
                  rightLabel={c.label}
                  onChange={(on) => act(`cap:${c.key}`, 'platformMerchantCapabilities', { capabilities: { [c.key]: Boolean(on) } })}
                  ariaLabel={c.label}
                />
                <p className="mt-1 text-xs opacity-60">{c.description}</p>
              </div>
            ))}
          </div>
        </SectionCard>
        <IndustriesCard key={(m.industries || []).join()} merchant={m} act={act} busy={busy} canManage={canManage} />
        <SectionCard title="Custom home page">
          <Switch
            checked={Number(m.custom_home_off) !== 1}
            disabled={!canManage || Boolean(busy)}
            rightLabel="Allow this store's custom HTML home"
            onChange={(on) => act('custom_home', 'platformMerchantCustomHome', { enabled: Boolean(on) })}
            ariaLabel="Allow this store's custom HTML home"
          />
          <p className="mt-1 text-xs opacity-60">Off: the store serves its standard home within a minute, whatever it has published. Use it if a store's own page harms shoppers.</p>
        </SectionCard>
      </div>
    </div>
  );
}
